import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import {
    ParsedData,
    TraceData,
    ExcelOutputData,
    JSONTrace,
    waterfallDict,
    TableData,
    MarkerDetails,
    WaterfallDataSingle,
    BwFeedbackRequest,
    FhSentimentAnalysisRequest,
    FhSentimentAnalysis,
    BwSummaryStatement,
} from './app/interfaces';
import { ApiClientService } from './app/lh-core/services/api-client.service';

@Injectable({
    providedIn: 'root',
})
export class BaseStockAndWaxesDataService {
    file: File | null = null;
    parsedData: ParsedData[] | null = null;
    selectedTrace: TraceData | null = null;
    months = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'August'];
    latestActual: number = 0;
    latestActualMonth: string = '';
    forecastMonths: string[] = [];
    m2Month: string = '';

    isDragging = false;
    loading = false;
    isTest = false;
    error: string | null = null;
    private destroy$ = new Subject<void>();
    private processedDataSource = new BehaviorSubject<ExcelOutputData | null>(
        null
    );
    private selectedItemSource = new BehaviorSubject<JSONTrace | null>(null);

    waterfalls: waterfallDict = {};

    selectedItem$ = this.selectedItemSource.asObservable();

    processedData$: Observable<ExcelOutputData | null> =
        this.processedDataSource.asObservable();

    constructor(private http: HttpClient, private api: ApiClientService) {}

    updateProcessedData(data: ExcelOutputData | null): void {
        this.processedDataSource.next(data);
    }

    clearData(): void {
        this.processedDataSource.next(null);
    }

    getCurrentProcessedData(): ExcelOutputData | null {
        return this.processedDataSource.getValue();
    }

    updateForecastMonths(item: JSONTrace | null): void {
        this.forecastMonths = [];
        let c = 0;
        if (item && this.months) {
            for (let i = 0; i < this.months.length; i++) {
                if (item.forecast_average[i] !== null) {
                    this.forecastMonths.push(this.months[i]);
                    if (c === 1) this.m2Month = this.months[i];
                    c++;
                }
            }
            for (let i = 0; i < this.months.length - 1; i++) {
                if (item.forecast_average[i + 1] !== null) {
                    this.latestActual = item.actual[i] ?? 0;
                    this.latestActualMonth = this.months[i];
                    break;
                }
            }
        }
    }

    getBaseAndWaxesInitialList() {
        return this.api.get<TableData[]>('bw/forecast');
    }

    getBaseAndWaxesMarkerDetails(marker: string) {
        return this.api.get<MarkerDetails>(`bw/${marker}`);
    }

    getWaterfallData(marker: string) {
        return this.api.get<WaterfallDataSingle[]>(`bw/impact/${marker}`);
    }

    getSentimentsAnalysis(data: FhSentimentAnalysisRequest): Observable<FhSentimentAnalysis[]> {
        return this.api.post<FhSentimentAnalysis[]>('bw/sentiments', data);
    }

    addBaseAndWavesFeedback(data: BwFeedbackRequest[]): Observable<void> {
        return this.api.post<void>('bw/feedback', data);
    }

    getSentimentsAnalysisBlobLink(blobPath?: string): Observable<string> {
        const params = blobPath ? { blobPath: blobPath } : null;
        return this.api.get<string>('bw/sentiments/blob-link', params);
    }

    getSummaryStatements(region?: string): Observable<BwSummaryStatement[]> {
        const params = region && region !== 'All' ? { region } : null;
        return this.api.get<BwSummaryStatement[]>('bw/summary-statements', params);
    }
}
